import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../stores/authStore';
import { useEffect, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import {
  WORK_EMAIL_REQUIRED_MESSAGE,
  exchangeSessionCookieDetailed,
  isWorkEmailRequiredExchange,
  setCachedAccessToken,
} from '../utils/api';
import { oauthAvatarUrl } from '../lib/oauthAvatarUrl';

interface Props {
  children: JSX.Element;
}

const AUTH_ENABLED = import.meta.env.VITE_AUTH_ENABLED !== 'false';

const ProtectedRoute = ({ children }: Props) => {
  const { user, login, logout } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    if (!AUTH_ENABLED || !supabase) {
      setChecking(false);
      return;
    }

    let cancelled = false;
    const bootstrap = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const sess = data?.session;
        if (sess?.access_token) setCachedAccessToken(sess.access_token);
        if (sess?.user && !cancelled) {
          const exchange = await exchangeSessionCookieDetailed();
          if (cancelled) return;
          console.log('[AUTH][PROTECTED] Session exchange:', exchange.ok, 'user:', sess.user.id);
          if (!exchange.ok) {
            setCachedAccessToken(null);
            await supabase.auth.signOut().catch(() => {});
            logout();
            if (isWorkEmailRequiredExchange(exchange)) {
              setAuthError(exchange.message || WORK_EMAIL_REQUIRED_MESSAGE);
            } else {
              setAuthError(exchange.message || 'Please sign in again to continue.');
            }
            setChecking(false);
            return;
          }
          const sessionUser = sess.user as any;
          const meta: any = sessionUser.user_metadata || {};
          const name = meta.full_name || meta.name || '';
          const avatarUrl = oauthAvatarUrl(sessionUser);
          login({
            id: sess.user.id,
            email: sess.user.email,
            name,
            avatar_url: avatarUrl,
            avatarUrl,
            user_metadata: meta,
            identities: sessionUser.identities,
          });
        }
      } catch (err) {
        console.error('[AUTH][PROTECTED] Bootstrap error:', err);
      }
      if (!cancelled) setChecking(false);
    };
    bootstrap();
    return () => { cancelled = true; };
  }, [login, location.pathname, logout]);

  if (!AUTH_ENABLED) return children;
  if (checking) {
    return (
      <div className="p-6 text-sm text-[#4b5563]">
        Loading…
      </div>
    );
  }
  if (authError) {
    const authIntent = window.sessionStorage.getItem('halr_auth_intent');
    window.sessionStorage.removeItem('halr_auth_intent');
    return (
      <Navigate
        to={authIntent === 'signup' ? '/signup' : '/login'}
        state={{ from: location, authError }}
        replace
      />
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
};

export default ProtectedRoute;
